import "@/App.css";
import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { useEffect, lazy, Suspense } from "react";
import { Toaster } from "./components/ui/sonner";
import { I18nProvider } from "./i18n/I18nContext";
import { AuthProvider } from "./context/AuthContext";
import { Header, BottomNav, Footer } from "./components/Layout";
import { ProtectedRoute, Loading } from "./components/common";
import Home from "./pages/Home";
import { ConsentGate } from "./components/Consent";

const named = (load, name) => lazy(() => load().then((m) => ({ default: m[name] })));
const assessment = () => import("./pages/Assessment");
const Assessment = named(assessment, "Assessment");
const AssessmentResults = named(assessment, "AssessmentResults");
const programs = () => import("./pages/Programs");
const Programs = named(programs, "Programs");
const ProgramDetail = named(programs, "ProgramDetail");
const auth = () => import("./pages/Auth");
const Login = named(auth, "Login");
const Signup = named(auth, "Signup");
const ForgotPassword = named(auth, "ForgotPassword");
const ResetPassword = named(auth, "ResetPassword");
const VerifyEmail = named(auth, "VerifyEmail");
const AuthCallback = named(auth, "AuthCallback");
const Dashboard = lazy(() => import("./pages/Dashboard"));
const MyProgram = lazy(() => import("./pages/MyProgram"));
const Lesson = named(() => import("./pages/MyProgram"), "Lesson");
const Activity = lazy(() => import("./pages/Activity"));
const Exercise = lazy(() => import("./pages/Exercise"));
const Exercises = named(() => import("./pages/Exercise"), "Exercises");
const Progress = lazy(() => import("./pages/Progress"));
const learn = () => import("./pages/Learn");
const Learn = named(learn, "Learn");
const Article = named(learn, "Article");
const Community = lazy(() => import("./pages/Community"));
const Ask = lazy(() => import("./pages/Ask"));
const Question = lazy(() => import("./pages/Question"));
const Coach = lazy(() => import("./pages/Coach"));
const Profile = lazy(() => import("./pages/Profile"));
const stat = () => import("./pages/Static");
const About = named(stat, "About");
const MedicalDisclaimer = named(stat, "MedicalDisclaimer");
const NotFound = named(stat, "NotFound");
const legal = () => import("./pages/Legal");
const Privacy = named(legal, "Privacy");
const Terms = named(legal, "Terms");
const Grievance = named(legal, "Grievance");
const admin = () => import("./pages/Admin");
const AdminOverview = named(admin, "AdminOverview");
const AdminModeration = named(admin, "AdminModeration");
const AdminContent = named(admin, "AdminContent");
const AdminAnalytics = named(admin, "AdminAnalytics");
const AdminReview = lazy(() => import("./pages/AdminReview"));

const P = (el) => <ProtectedRoute>{el}</ProtectedRoute>;
const A = (el) => <ProtectedRoute admin>{el}</ProtectedRoute>;

function ScrollTop() {
  const { pathname } = useLocation();
  useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
}

const sensitive = (p) => (window.ckbSensitivePath ? window.ckbSensitivePath(p) : true);

function RecordingGate() {
  const { pathname } = useLocation();
  useEffect(() => {
    const ph = window.posthog;
    if (!ph?.__loaded) return;
    if (sensitive(pathname)) ph.stopSessionRecording();
    else ph.startSessionRecording();
  }, [pathname]);
  return null;
}

function Main() {
  const { pathname } = useLocation();
  return (
    <div id="main" className={`flex-1${sensitive(pathname) ? " ph-no-capture" : ""}`}>
      <Suspense fallback={<Loading />}><AppRouter /></Suspense>
    </div>
  );
}

function pub(path, el) {
  return ["", "/hi", "/hinglish"].map((p) => <Route key={p + path} path={p ? (path === "/" ? p : p + path) : path} element={el} />);
}

function AppRouter() {
  const location = useLocation();
  if (location.hash?.includes("session_id=")) return <Suspense fallback={<Loading />}><AuthCallback /></Suspense>;
  return (
    <Routes>
      {pub("/", <Home />)}
      {pub("/assessment", <Assessment />)}
      <Route path="/assessment/results" element={<AssessmentResults />} />
      {pub("/programs", <Programs />)}
      {pub("/programs/:slug", <ProgramDetail />)}
      <Route path="/login" element={<Login />} />
      <Route path="/signup" element={<Signup />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />
      <Route path="/verify-email" element={<VerifyEmail />} />
      <Route path="/dashboard" element={P(<Dashboard />)} />
      <Route path="/my-program" element={P(<MyProgram />)} />
      <Route path="/my-program/lesson" element={P(<Lesson />)} />
      <Route path="/my-program/activity" element={P(<Activity />)} />
      <Route path="/exercises" element={<Exercises />} />
      <Route path="/exercise/:id" element={<Exercise />} />
      <Route path="/progress" element={P(<Progress />)} />
      {pub("/community", <Community />)}
      <Route path="/community/ask" element={P(<Ask />)} />
      {pub("/community/question/:slug", <Question />)}
      {pub("/learn", <Learn />)}
      {pub("/learn/article/:slug", <Article />)}
      {pub("/learn/:category", <Learn />)}
      <Route path="/ai-coach" element={P(<Coach />)} />
      <Route path="/profile" element={P(<Profile />)} />
      <Route path="/settings" element={<Navigate to="/profile" replace />} />
      {pub("/about", <About />)}
      {pub("/privacy", <Privacy />)}
      {pub("/terms", <Terms />)}
      {pub("/grievance", <Grievance />)}
      {pub("/medical-disclaimer", <MedicalDisclaimer />)}
      <Route path="/admin" element={A(<AdminOverview />)} />
      <Route path="/admin/moderation" element={A(<AdminModeration />)} />
      <Route path="/admin/review" element={A(<AdminReview />)} />
      <Route path="/admin/content" element={A(<AdminContent />)} />
      <Route path="/admin/analytics" element={A(<AdminAnalytics />)} />
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <I18nProvider>
        <AuthProvider>
          <ScrollTop />
          <RecordingGate />
          <div className="flex min-h-screen flex-col bg-ckb-sand">
            <a href="#main" className="sr-only focus:not-sr-only focus:absolute focus:left-2 focus:top-2 focus:z-50 focus:rounded focus:bg-white focus:p-2">Skip to content</a>
            <Header />
            <Main />
            <Footer />
            <BottomNav />
          </div>
          <ConsentGate />
          <Toaster position="top-center" richColors />
        </AuthProvider>
      </I18nProvider>
    </BrowserRouter>
  );
}
